import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { useEffect, useState, useCallback } from 'react';

export const useAuth = () => {
  const clerkAuth = useClerkAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenRefreshInterval, setTokenRefreshInterval] = useState(null);

  // Token refresh function
  const refreshToken = async () => {
    if (clerkAuth.isSignedIn && userLoaded) {
      try {
        const token = await clerkAuth.getToken();
        setAuthToken(token);
        return token;
      } catch (error) {
        console.error('Error refreshing auth token:', error);
        // Clear token on error
        setAuthToken(null);
        return null;
      }
    }
    return null;
  };

  // Initial token fetch and setup refresh interval
  useEffect(() => {
    const getToken = async () => {
      if (clerkAuth.isSignedIn && userLoaded) {
        try {
          const token = await clerkAuth.getToken();
          setAuthToken(token);
          
          // Set up token refresh interval (every 25 minutes to ensure refresh before 1-hour expiry)
          const interval = setInterval(refreshToken, 25 * 60 * 1000);
          setTokenRefreshInterval(interval);
        } catch (error) {
          console.error('Error getting auth token:', error);
        }
      }
      setLoading(false);
    };

    if (clerkAuth.isLoaded) {
      getToken();
    }

    // Cleanup interval on unmount
    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, [clerkAuth.isLoaded, clerkAuth.isSignedIn, userLoaded]);

  // Clear token and interval on sign out
  useEffect(() => {
    if (!clerkAuth.isSignedIn) {
      setAuthToken(null);
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        setTokenRefreshInterval(null);
      }
    }
  }, [clerkAuth.isSignedIn, tokenRefreshInterval]);

  return {
    // Clerk auth state
    isLoaded: clerkAuth.isLoaded,
    isSignedIn: clerkAuth.isSignedIn,
    user: user,
    userId: user?.id,
    
    // Auth token for API calls
    token: authToken,
    
    // Loading state
    loading: loading || !clerkAuth.isLoaded,
    
    // Auth methods (memoized to prevent unnecessary re-renders)
    signOut: clerkAuth.signOut,
    getToken: useCallback((options) => clerkAuth.getToken(options), [clerkAuth.getToken]),
    refreshToken: refreshToken,
    
    // User info helpers
    userEmail: user?.primaryEmailAddress?.emailAddress,
    userName: user?.fullName || user?.firstName || user?.primaryEmailAddress?.emailAddress,
    userInitials: user?.fullName 
      ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase()
      : user?.firstName?.[0]?.toUpperCase() || '?',
    
    // Check if user is authenticated and ready
    isAuthenticated: clerkAuth.isSignedIn && userLoaded && !loading,
    
    // Get user preferences key
    getUserPreferencesKey: () => user?.id ? `zonewise_preferences_${user.id}` : 'zonewise_preferences'
  };
};

export default useAuth;